import { useEffect, useState } from 'react';
import { api, ApiError } from './api';
import { STOREFRONT_HOST, STOREFRONT_URL } from './lib';

// The two screens someone sees before they are staff. The admin center has no sign-in page of its
// own: it reads the order.tavezio.io session (admin/src/auth.ts identityOf), so a signed-out visitor
// is sent there. A signed-in person who is not on the staff list redeems an invite code an admin
// minted in Staff → Directory (POST /staff/join; admin/src/routes/staff.ts), which is how anyone
// joins it. Before this screen, both ended on a bare "Admin API error".
//
// An invite link carries its code after `#` (Staff → Directory builds it: #invite=CODE), so the code
// never reaches a server log or a Referer header. A signed-out visitor who follows the link is sent
// away to sign in, so the code is also kept in this browser until they come back and join.

const KEPT_INVITE = 'tavezio-admin-invite';

const fromHash = () => new URLSearchParams(window.location.hash.replace(/^#/, '')).get('invite')?.trim().toUpperCase() || '';

/** The invite code in the link, or one kept from an earlier visit. */
function inviteCode(): string {
  const code = fromHash();
  try {
    if (code) localStorage.setItem(KEPT_INVITE, code);
    return code || localStorage.getItem(KEPT_INVITE) || '';
  } catch {
    return code; // storage can be blocked; the link still works
  }
}

function forgetInvite() {
  try { localStorage.removeItem(KEPT_INVITE); } catch { /* nothing kept */ }
  if (window.location.hash) history.replaceState(null, '', window.location.pathname + window.location.search);
}

export function SignedOut() {
  const hasInvite = Boolean(inviteCode());
  return (
    <div className="gate">
      <section className="card">
        <h1>Sign in first</h1>
        <p>The admin center uses your {STOREFRONT_HOST} sign-in. Sign in there, then come back to this page in the same browser.</p>
        {hasInvite && <p className="muted">Your invite code is saved in this browser, so you won't need to find it again.</p>}
        <a className="btn-sm" href={STOREFRONT_URL}>Sign in at {STOREFRONT_HOST}</a>
      </section>
    </div>
  );
}

const joinError = (e: unknown) => {
  const status = e instanceof ApiError ? e.status : 0;
  if (status === 404) return "That code doesn't work: it's mistyped, already used, or expired. Ask an admin for a new one.";
  if (status === 401) return `Your sign-in has ended. Sign in at ${STOREFRONT_HOST} again, then come back.`;
  return `Couldn't join: ${(e as Error).message}`;
};

export function JoinStaff({ onJoined }: { onJoined: () => void }) {
  const [code, setCode] = useState(inviteCode);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // An invite link pasted into a tab that already shows this screen changes only the `#` part,
  // which does not reload the page.
  useEffect(() => {
    const onHash = () => { const c = inviteCode(); if (c) setCode(c); };
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  const join = async () => {
    const c = code.trim().toUpperCase();
    if (!c) return setErr('Enter the invite code an admin gave you.');
    setBusy(true); setErr(null);
    try {
      await api.join(c, name.trim());
      forgetInvite();
      onJoined();
    } catch (e) {
      setErr(joinError(e));
      setBusy(false);
    }
  };

  return (
    <div className="gate">
      <section className="card">
        <h1>Join the admin center</h1>
        <p>You're signed in, but you're not on the admin center's staff list yet. Enter the invite code an admin gave you.</p>
        <form onSubmit={(e) => { e.preventDefault(); void join(); }}>
          <label className="field"><span>Invite code</span>
            <input className="search mono-input" value={code} autoComplete="off" spellCheck={false} maxLength={16}
              onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="e.g. K7Q2M9XA" />
          </label>
          <label className="field"><span>Your name</span>
            <input className="search" value={name} autoComplete="name" onChange={(e) => setName(e.target.value)}
              placeholder="How other staff will see you" />
          </label>
          <button className="btn-sm" type="submit" disabled={busy}>{busy ? 'Joining…' : 'Join'}</button>
        </form>
        {err && <p className="msg bad">{err}</p>}
        <p className="muted small">No code? An admin or manager creates one in Staff → Directory. If your access was turned off, a new code turns it back on.</p>
      </section>
    </div>
  );
}
